/**
 * @module hooks
 * @package @geenius/adapters/react-native
 * @description Defines React hooks for reading adapter instances and status
 * state from the React Native provider context.
 */

import type {
  AdapterDomain,
  AdapterStatusInfo,
  AdminAdapter,
  AiAdapter,
  AuthAdapter,
  DbAdapter,
  FileStorageAdapter,
  LoggerAdapter,
  PaymentsAdapter,
} from "@geenius/adapters";
import { useMemo } from "react";
import { useAdapterContext } from "../provider";

/**
 * Reads the configured database adapter from the nearest native provider.
 *
 * @returns The DB adapter registered for the `db` domain.
 * @example
 * ```tsx
 * const db = useDb();
 * const users = await db.list("users");
 * ```
 */
export function useDb(): DbAdapter {
  return useAdapterContext().getAdapter("db");
}

/**
 * Reads the configured auth adapter from the nearest native provider.
 *
 * @returns The auth adapter registered for the `auth` domain.
 * @example
 * ```tsx
 * const auth = useAuth();
 * const session = await auth.getSession();
 * ```
 */
export function useAuth(): AuthAdapter {
  return useAdapterContext().getAdapter("auth");
}

/**
 * Alias for {@link useAuth} retained for consumers that prefer explicit names.
 *
 * @returns The auth adapter registered for the `auth` domain.
 * @example
 * ```tsx
 * const auth = useAuthAdapter();
 * ```
 */
export function useAuthAdapter(): AuthAdapter {
  return useAuth();
}

/**
 * Reads the configured payments adapter from the nearest native provider.
 *
 * @returns The payments adapter registered for the `payments` domain.
 * @example
 * ```tsx
 * const payments = usePayments();
 * const plans = await payments.listPlans();
 * ```
 */
export function usePayments(): PaymentsAdapter {
  return useAdapterContext().getAdapter("payments");
}

/**
 * Reads the configured AI adapter from the nearest native provider.
 *
 * @returns The AI adapter registered for the `ai` domain.
 * @example
 * ```tsx
 * const ai = useAi();
 * const answer = await ai.complete({ prompt: "Summarize" });
 * ```
 */
export function useAi(): AiAdapter {
  return useAdapterContext().getAdapter("ai");
}

/**
 * Reads the configured file storage adapter from the nearest native provider.
 *
 * @returns The storage adapter registered for the `storage` domain.
 * @example
 * ```tsx
 * const storage = useFileStorage();
 * await storage.upload({ path: "avatars/a.png", data });
 * ```
 */
export function useFileStorage(): FileStorageAdapter {
  return useAdapterContext().getAdapter("storage");
}

/**
 * Alias for {@link useFileStorage} used by storage-focused screens.
 *
 * @returns The storage adapter registered for the `storage` domain.
 * @example
 * ```tsx
 * const storage = useStorage();
 * ```
 */
export function useStorage(): FileStorageAdapter {
  return useFileStorage();
}

/**
 * Reads the configured admin adapter from the nearest native provider.
 *
 * @returns The admin adapter registered for the `admin` domain.
 * @example
 * ```tsx
 * const admin = useAdmin();
 * const roles = await admin.listRoles();
 * ```
 */
export function useAdmin(): AdminAdapter {
  return useAdapterContext().getAdapter("admin");
}

/**
 * Reads the configured logger adapter from the nearest native provider.
 *
 * @returns The logger adapter registered for the `logger` domain.
 * @example
 * ```tsx
 * const logger = useLogger();
 * logger.info("native screen mounted");
 * ```
 */
export function useLogger(): LoggerAdapter {
  return useAdapterContext().getAdapter("logger");
}

/**
 * Reads the status map for every adapter domain.
 *
 * @returns A record keyed by adapter domain with provider status metadata.
 * @example
 * ```tsx
 * const statuses = useAdapterStatuses();
 * const dbStatus = statuses.db.status;
 * ```
 */
export function useAdapterStatuses(): Record<AdapterDomain, AdapterStatusInfo> {
  return useAdapterContext().statuses;
}

/**
 * Reads the status metadata for a single adapter domain.
 *
 * @param domain Adapter domain to inspect.
 * @returns The status payload for the requested domain.
 * @example
 * ```tsx
 * const status = useAdapterStatus("auth");
 * ```
 */
export function useAdapterStatus(domain: AdapterDomain): AdapterStatusInfo {
  return useAdapterContext().statuses[domain];
}

/**
 * Reports whether a configured adapter is currently connected.
 *
 * @param domain Adapter domain to inspect.
 * @returns `true` when the domain has an adapter and its status is connected.
 * @example
 * ```tsx
 * const canSubmit = useIsAdapterReady("payments");
 * ```
 */
export function useIsAdapterReady(domain: AdapterDomain): boolean {
  return useAdapterContext().isReady(domain);
}

/**
 * Reads the full native adapter bundle and provider status helpers.
 *
 * @returns Adapter instances, status records, loading state, and readiness helper.
 * @example
 * ```tsx
 * const { db, statuses, isReady } = useAdapters();
 * ```
 */
export function useAdapters(): {
  admin: AdminAdapter;
  ai: AiAdapter;
  auth: AuthAdapter;
  db: DbAdapter;
  fileStorage: FileStorageAdapter;
  isLoading: boolean;
  isReady: (domain: AdapterDomain) => boolean;
  logger: LoggerAdapter;
  payments: PaymentsAdapter;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  storage: FileStorageAdapter;
} {
  const context = useAdapterContext();

  return useMemo(
    () => ({
      db: context.adapters.db,
      auth: context.adapters.auth,
      payments: context.adapters.payments,
      ai: context.adapters.ai,
      fileStorage: context.adapters.fileStorage,
      storage: context.adapters.storage,
      admin: context.adapters.admin,
      logger: context.adapters.logger,
      statuses: context.statuses,
      isLoading: context.isLoading,
      isReady: context.isReady,
    }),
    [context],
  );
}
